import axios from 'axios';
import { useAuthStore } from '../store/authStore';
import { getAuthFailureRedirectPath } from '../lib/authFailureRedirect';
import { reportErrorToBackend } from '../lib/errorCapture';

// Create axios instance
// In development, use relative URL to leverage Vite proxy
// In production, use VITE_API_URL or default to /api
const getBaseURL = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Use relative URL in development to leverage Vite proxy
  if (import.meta.env.DEV) {
    return '/api';
  }
  // Production fallback
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
api.interceptors.response.use(
  (response) => {
    // Don't process blob responses - return as-is
    if (response.config.responseType === 'blob') {
      return response;
    }
    return response;
  },
  (error) => {
    if (error.response) {
      // Handle 401 Unauthorized - logout and redirect to login
      if (error.response.status === 401) {
        const user = useAuthStore.getState().user;
        const path = getAuthFailureRedirectPath(user);
        useAuthStore.getState().logout();
        window.location.href = path;
        return Promise.reject(error);
      }
      // Handle 403 when token is invalid/expired (server returns 403 for jwt.verify failure)
      if (error.response.status === 403) {
        const msg = (error.response.data?.error || error.response.data?.message || '').toLowerCase();
        if (msg.includes('expired') || msg.includes('invalid') && msg.includes('token')) {
          const user = useAuthStore.getState().user;
          const path = getAuthFailureRedirectPath(user);
          useAuthStore.getState().logout();
          window.location.href = path;
          return Promise.reject(error);
        }
      }

      // Report 5xx errors to ingest (fire-and-forget)
      const status = error.response?.status;
      if (status >= 500) {
        const requestId = error.response?.headers?.['x-request-id'];
        const config = error.config;
        reportErrorToBackend({
          message: error.message || `Server error (${status})`,
          statusCode: status || null,
          route: config?.url,
          method: config?.method?.toUpperCase?.(),
          requestId: requestId || undefined,
          buildVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
        });
      }

      // If responseType is blob, the error data might be a blob containing JSON
      if (error.config?.responseType === 'blob' && error.response.data instanceof Blob) {
        // Try to read the blob as text to get error message
        return error.response.data.text().then(text => {
          try {
            const errorData = JSON.parse(text);
            const errorMsg = errorData.error || errorData.message || `Server error (${error.response.status})`;
            const enhancedError = new Error(errorMsg);
            enhancedError.response = error.response;
            enhancedError.status = error.response.status;
            enhancedError.originalError = errorData;
            console.error('Blob error response:', errorData);
            return Promise.reject(enhancedError);
          } catch (parseErr) {
            // If parsing fails, return generic error with status
            const enhancedError = new Error(`Server error (${error.response.status}): ${text.substring(0, 100)}`);
            enhancedError.response = error.response;
            enhancedError.status = error.response.status;
            console.error('Failed to parse blob error:', parseErr, 'Text:', text.substring(0, 200));
            return Promise.reject(enhancedError);
          }
        }).catch(blobErr => {
          // If reading blob fails, return generic error
          const enhancedError = new Error(`Server error (${error.response.status})`);
          enhancedError.response = error.response;
          enhancedError.status = error.response.status;
          console.error('Failed to read blob error:', blobErr);
          return Promise.reject(enhancedError);
        });
      }
      
      // Preserve original error structure by attaching response to the error
      // Support express-validator format: { errors: [{ msg, path, ... }] }
      const data = error.response.data;
      const firstValidationMsg = Array.isArray(data?.errors) ? data.errors[0]?.msg : undefined;
      const message = data?.error || data?.message || firstValidationMsg || 'An error occurred';
      const enhancedError = new Error(message);
      enhancedError.response = error.response; // Preserve response for detailed error handling
      enhancedError.status = error.response.status;
      return Promise.reject(enhancedError);
    }

    // Network error - report to ingest (fire-and-forget)
    reportErrorToBackend({
      message: error.message || 'Network error. Please check your connection.',
      route: error.config?.url,
      method: error.config?.method?.toUpperCase?.(),
      buildVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
    });
    const err = new Error('Network error. Please check your connection.');
    err.code = error.code; // ECONNREFUSED, ETIMEDOUT, ECONNABORTED, etc.
    err.cause = error;
    return Promise.reject(err);
  }
);

// API endpoints
export const authAPI = {
  login: (credentials) =>
    api.post('/auth/login', credentials, { timeout: 15000 }).then(r => r.data),
  /** Slug + email + password (client_contacts). Body: { portalSlug, email, password } */
  portalLogin: (body) => api.post('/portal/auth/login', body).then((r) => r.data),
  loginWithPortalToken: (portalToken) =>
    api.post('/portal/auth/login-with-token', { portalToken }).then((r) => r.data),
  register: (userData) => api.post('/auth/register', userData).then(r => r.data),
  me: () => api.get('/auth/me').then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),

  // WebAuthn / Passkeys
  webauthnRegisterOptions: () =>
    api.post('/auth/webauthn/register-options').then(r => r.data),
  webauthnRegisterVerify: (body) =>
    api.post('/auth/webauthn/register-verify', body).then(r => r.data),
  webauthnAuthenticateOptions: (email) =>
    api.post('/auth/webauthn/authenticate-options', { email }).then(r => r.data),
  webauthnAuthenticateVerify: (body) =>
    api.post('/auth/webauthn/authenticate-verify', body).then(r => r.data),
  webauthnCredentials: () =>
    api.get('/auth/webauthn/credentials').then(r => r.data),
  webauthnCredentialDelete: (id) =>
    api.delete(`/auth/webauthn/credentials/${id}`).then(r => r.data),
};

/** Client portal branding (tenant logo + company name). Requires authenticated client role. */
export const portalBrandingAPI = {
  getBranding: () => api.get('/portal/branding').then(r => r.data),
};

/** Client portal – my invoices, estimates, certificates (authenticated client). */
export const clientPortalAPI = {
  getMe: () => api.get('/portal/me').then((r) => r.data),
  completeOnboarding: (body) => api.post('/portal/onboarding/complete', body).then((r) => r.data),
  getMyInvoices: () => api.get('/portal/my-invoices').then(r => r.data),
  getMyEstimates: () => api.get('/portal/my-estimates').then(r => r.data),
  getMyCertificates: () => api.get('/portal/my-certificates').then(r => r.data),
  changePassword: (body) => api.post('/portal/change-password', body).then((r) => r.data),
  updateMe: (body) => api.put('/portal/me', body).then((r) => r.data),
  sendMessage: (body) => api.post('/portal/messages', body).then((r) => r.data),
  getMyStatementHtml: (params) =>
    api.get('/portal/my-statement/html', { params, responseType: 'text' }).then((r) => r.data),
  getMyStatementPdfBlob: (params) =>
    api.get('/portal/my-statement/pdf', { params, responseType: 'blob' }).then((r) => r.data),
};

/** Tenant admin invite onboarding (public; raw token in query/body only). */
export const publicTenantInviteAPI = {
  getPreview: (token) =>
    api.get('/public/tenant-invite', { params: { token } }).then((r) => r.data),
  complete: (body) => api.post('/public/tenant-invite/complete', body).then((r) => r.data),
};

export const tenantClientPortalMessagesAPI = {
  list: (params = {}) => api.get('/client-portal-messages', { params }).then((r) => r.data),
  markViewed: (id) => api.patch(`/client-portal-messages/${id}/viewed`).then((r) => r.data),
};

export const tenantsAPI = {
  getAll: () => api.get('/tenants').then(r => r.data),
  getOne: (id) => api.get(`/tenants/${id}`).then(r => r.data),
  create: (data) => api.post('/tenants', data).then(r => r.data),
  update: (id, data) => api.put(`/tenants/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/tenants/${id}`).then(r => r.data),
};

export const clientsAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.client_type) queryParams.append('client_type', params.client_type);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    const queryString = queryParams.toString();
    return api.get(`/clients${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/clients/${id}`).then(r => r.data),
  generateStatementLink: (id, body) =>
    api.post(`/clients/${id}/generate-statement-link`, body).then((r) => r.data),
  sendStatementEmail: (id, body) =>
    api.post(`/clients/${id}/send-statement-email`, body).then((r) => r.data),
  create: (data) => api.post('/clients', data).then(r => r.data),
  update: (id, data) => api.put(`/clients/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/clients/${id}`).then(r => r.data),
  importPreview: (file, mappings) => {
    const formData = new FormData();
    formData.append('file', file);
    if (mappings) {
      formData.append('mappings', JSON.stringify(mappings));
    }
    return api.post('/clients/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  importExecute: (rows) => {
    return api.post('/clients/import/execute', { rows }).then(r => r.data);
  },
  importContactsPreview: (file, mappings) => {
    const formData = new FormData();
    formData.append('file', file);
    if (mappings) {
      formData.append('mappings', JSON.stringify(mappings));
    }
    return api.post('/clients/contacts/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  importContactsExecute: (rows) => {
    return api.post('/clients/contacts/import/execute', { rows }).then(r => r.data);
  },
};

export const invoicesAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.clientId) queryParams.append('client_id', params.clientId);
    if (params.view) queryParams.append('view', params.view);
    const queryString = queryParams.toString();
    return api.get(`/invoices${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/invoices/${id}`).then(r => r.data),
  /** Same HTML template as PDF / portal print view; requires Authorization */
  getHtml: (id) =>
    api.get(`/invoices/${id}/html`, { responseType: 'text' }).then((r) => r.data),
  getNextNumber: () => api.get('/invoices/next-number').then(r => r.data),
  checkNumber: (invoiceNum, excludeId) => {
    const params = excludeId ? `?excludeId=${excludeId}` : '';
    return api.get(`/invoices/check-number/${encodeURIComponent(invoiceNum)}${params}`).then(r => r.data);
  },
  create: (data) => api.post('/invoices', data).then(r => r.data),
  update: (id, data) => api.put(`/invoices/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/invoices/${id}`).then(r => r.data),
  archive: (id, isArchived) => api.patch(`/invoices/${id}/archive`, { isArchived }).then(r => r.data),
  send: (id) => api.post(`/invoices/${id}/send`).then(r => r.data),
  duplicate: (id) => api.post(`/invoices/${id}/duplicate`).then((r) => r.data),
  generateLink: (id, expiresInDays = 30) => api.post(`/invoices/${id}/generate-link`, { expiresInDays }).then(r => r.data),
  sendEmail: (id, email, thirdArg) => {
    let expiresInDays = 30;
    let extra = {};
    if (typeof thirdArg === 'number') {
      expiresInDays = thirdArg;
    } else if (thirdArg && typeof thirdArg === 'object') {
      const { expiresInDays: exp = 30, ...rest } = thirdArg;
      expiresInDays = exp;
      extra = rest;
    }
    return api.post(`/invoices/${id}/send-email`, { email, expiresInDays, ...extra }).then((r) => r.data);
  },
  getCertificates: (id) => api.get(`/invoices/${id}/certificates`).then(r => r.data),
  linkCertificate: (id, certificateId) => api.post(`/invoices/${id}/certificates`, { certificateId }).then(r => r.data),
  unlinkCertificate: (id, certificateId) => api.delete(`/invoices/${id}/certificates/${certificateId}`).then(r => r.data),
};

// Portal API (no auth required for public links)
const portalApi = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Unauthenticated portal helpers (tenant branding by slug). */
export const portalPublicAPI = {
  getBranding: (slug) =>
    portalApi
      .get(`/portal/public/branding?slug=${encodeURIComponent(slug)}`)
      .then((r) => r.data),
  /** Body: { portalSlug, email } */
  forgotPassword: (body) =>
    portalApi.post('/portal/auth/forgot-password', body).then((r) => r.data),
  /** Body: { portalSlug, token, newPassword } */
  resetPassword: (body) =>
    portalApi.post('/portal/auth/reset-password', body).then((r) => r.data),
};

export const portalAPI = {
  getInvoiceByToken: (token) => portalApi.get(`/portal/invoice/${token}`).then(r => r.data),
  downloadInvoicePDF: (token) => portalApi.get(`/portal/invoice/${token}/pdf`, { responseType: 'blob' }).then(r => r.data),
  getEstimateByToken: (token) => portalApi.get(`/portal/estimate/${token}`).then(r => r.data),
  acceptEstimateByToken: (token) => portalApi.post(`/portal/estimate/${token}/accept`).then((r) => r.data),
  rejectEstimateByToken: (token, body) =>
    portalApi.post(`/portal/estimate/${token}/reject`, body || {}).then((r) => r.data),
  queryEstimateByToken: (token, body) =>
    portalApi.post(`/portal/estimate/${token}/query`, body || {}).then((r) => r.data),
  createCheckout: (token) => portalApi.post(`/portal/invoice/${token}/create-checkout`).then(r => r.data),
  /** Body: { sessionId } — reconcile Stripe Checkout after success redirect (idempotent). */
  syncStripeCheckoutSession: (token, sessionId) =>
    portalApi
      .post(`/portal/invoice/${token}/sync-stripe-session`, { sessionId })
      .then((r) => r.data),
};

export const feedbackAPI = {
  submit: ({ message, type, sourcePage }) => api.post('/feedback', { message, type, sourcePage: sourcePage || undefined }).then(r => r.data),
};

export const estimatesAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.clientId) queryParams.append('client_id', params.clientId);
    if (params.view) queryParams.append('view', params.view);
    const queryString = queryParams.toString();
    return api.get(`/estimates${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/estimates/${id}`).then(r => r.data),
  getHtml: (id) =>
    api.get(`/estimates/${id}/html`, { responseType: 'text' }).then((r) => r.data),
  getNextNumber: () => api.get('/estimates/next-number').then((r) => r.data),
  checkNumber: (estimateNum, excludeId) => {
    const params = excludeId ? `?excludeId=${excludeId}` : '';
    return api.get(`/estimates/check-number/${encodeURIComponent(estimateNum)}${params}`).then((r) => r.data);
  },
  create: (data) => api.post('/estimates', data).then(r => r.data),
  update: (id, data) => api.put(`/estimates/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/estimates/${id}`).then(r => r.data),
  archive: (id, isArchived) => api.patch(`/estimates/${id}/archive`, { isArchived }).then(r => r.data),
  send: (id) => api.post(`/estimates/${id}/send`).then(r => r.data),
  reissue: (id) => api.post(`/estimates/${id}/reissue`).then((r) => r.data),
  duplicate: (id) => api.post(`/estimates/${id}/duplicate`).then((r) => r.data),
  acknowledgeClientResponse: (id) =>
    api.post(`/estimates/${id}/acknowledge-client-response`).then((r) => r.data),
  generateLink: (id, expiresInDays = 30) => api.post(`/estimates/${id}/generate-link`, { expiresInDays }).then(r => r.data),
  sendEmail: (id, email, thirdArg) => {
    let expiresInDays = 30;
    if (typeof thirdArg === 'number') expiresInDays = thirdArg;
    else if (thirdArg && typeof thirdArg === 'object') {
      const { expiresInDays: exp = 30, ...rest } = thirdArg;
      return api.post(`/estimates/${id}/send-email`, { email, expiresInDays: exp, ...rest }).then((r) => r.data);
    }
    return api.post(`/estimates/${id}/send-email`, { email, expiresInDays }).then((r) => r.data);
  },
  convertToInvoice: (id, invoiceNum, dueDate) => {
    const body = {};
    if (invoiceNum != null && String(invoiceNum).trim() !== '') {
      body.invoiceNum = String(invoiceNum).trim();
    }
    if (dueDate != null && dueDate !== '') {
      body.dueDate = dueDate;
    }
    return api.post(`/estimates/${id}/convert-to-invoice`, body).then((r) => r.data);
  },
};

export const paymentsAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.invoice_id) queryParams.append('invoice_id', params.invoice_id);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return api.get(`/payments${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/payments/${id}`).then(r => r.data),
  create: (data) => api.post('/payments', data).then(r => r.data),
  createBulk: (data) => api.post('/payments/bulk', data).then(r => r.data),
  update: (id, data) => api.put(`/payments/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/payments/${id}`).then(r => r.data),
  createStripeCheckout: (invoiceId) => api.post('/payments/stripe/create-checkout', { invoice_id: invoiceId }).then(r => r.data),
  createSumUpCheckout: (invoiceId) => api.post('/payments/sumup/create-checkout', { invoice_id: invoiceId }).then(r => r.data),
};

export const suppliersAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    const queryString = queryParams.toString();
    return api.get(`/suppliers${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/suppliers/${id}`).then(r => r.data),
  create: (data) => api.post('/suppliers', data).then(r => r.data),
  update: (id, data) => api.put(`/suppliers/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/suppliers/${id}`).then(r => r.data),
  searchPicker: (q) => api.get(`/suppliers/search/picker?q=${encodeURIComponent(q)}`).then(r => r.data),
  importPreview: (file, mappings) => {
    const formData = new FormData();
    formData.append('file', file);
    if (mappings) {
      formData.append('mappings', JSON.stringify(mappings));
    }
    return api.post('/suppliers/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  importExecute: (rows) => {
    return api.post('/suppliers/import/execute', { rows }).then(r => r.data);
  },
};

export const purchasesAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.supplier_id) queryParams.append('supplier_id', params.supplier_id);
    if (params.isArchived !== undefined) queryParams.append('is_archived', params.isArchived);
    if (params.isDeleted !== undefined) queryParams.append('is_deleted', params.isDeleted);
    const queryString = queryParams.toString();
    return api.get(`/purchases${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/purchases/${id}`).then(r => r.data),
  create: (data) => api.post('/purchases', data).then(r => r.data),
  update: (id, data) => api.put(`/purchases/${id}`, data).then(r => r.data),
  delete: (id, options = {}) => {
    const params = new URLSearchParams();
    if (options.paymentDisposition) params.set('payment_disposition', options.paymentDisposition);
    const qs = params.toString();
    return api.delete(`/purchases/${id}${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  archive: (id, isArchived) => api.patch(`/purchases/${id}/archive`, { is_archived: isArchived }).then(r => r.data),
  markReceived: (id) => api.post(`/purchases/${id}/mark-received`).then(r => r.data),
  duplicate: (id) => api.post(`/purchases/${id}/duplicate`).then(r => r.data),
  getMaterialsLookup: (q, limit = 20) => api.get(`/purchases/materials/lookup?q=${encodeURIComponent(q)}&limit=${limit}`).then(r => r.data),
  getPrimaryAttachmentFile: (purchaseId) => api.get(`/purchases/${purchaseId}/primary-attachment/file`, { responseType: 'blob' }).then(r => r.data),
  getAttachments: (purchaseId) => api.get(`/purchases/${purchaseId}/attachments`).then(r => r.data),
  uploadAttachment: (purchaseId, file, description = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('description', description);
    return api.post(`/purchases/${purchaseId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },
  getAttachmentFile: (purchaseId, attachmentId) =>
    api.get(`/purchases/${purchaseId}/attachments/${attachmentId}/file`, { responseType: 'blob' }).then(r => r.data),
  deleteAttachment: (purchaseId, attachmentId) =>
    api.delete(`/purchases/${purchaseId}/attachments/${attachmentId}`).then(r => r.data),
};

export const aiPurchasesAPI = {
  upload: (file, selectedPages = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (selectedPages && Array.isArray(selectedPages)) {
      formData.append('selectedPages', JSON.stringify(selectedPages));
    }
    return api.post('/ai-purchases/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(r => r.data);
  },
  getDrafts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return api.get(`/ai-purchases/drafts${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getDraft: (id) => api.get(`/ai-purchases/drafts/${id}`).then(r => r.data),
  getDraftFile: (id) => api.get(`/ai-purchases/drafts/${id}/file`, { responseType: 'blob' }).then(r => r.data),
  approve: (id) => api.put(`/ai-purchases/drafts/${id}/approve`).then(r => r.data),
  reject: (id, notes) => api.put(`/ai-purchases/drafts/${id}/reject`, { notes }).then(r => r.data),
  update: (id, data) => api.put(`/ai-purchases/drafts/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/ai-purchases/drafts/${id}`).then(r => r.data),
};

export const purchasePaymentsAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.purchase_id) queryParams.append('purchase_id', params.purchase_id);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return api.get(`/purchase-payments${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/purchase-payments/${id}`).then(r => r.data),
  create: (data) => api.post('/purchase-payments', data).then(r => r.data),
  createBatch: (data) => api.post('/purchase-payments/batch', data).then(r => r.data),
  delete: (id) => api.delete(`/purchase-payments/${id}`).then(r => r.data),
};

export const bankingAPI = {
  // Bank Accounts
  getAccounts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.include_inactive !== undefined) queryParams.append('include_inactive', params.include_inactive);
    const queryString = queryParams.toString();
    return api.get(`/banking/accounts${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getAccount: (id) => api.get(`/banking/accounts/${id}`).then(r => r.data),
  createAccount: (data) => api.post('/banking/accounts', data).then(r => r.data),
  updateAccount: (id, data) => api.put(`/banking/accounts/${id}`, data).then(r => r.data),
  deleteAccount: (id) => api.delete(`/banking/accounts/${id}`).then(r => r.data),
  
  // Bank Transactions
  getTransactions: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.bank_account_id) queryParams.append('bank_account_id', params.bank_account_id);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return api.get(`/banking/transactions${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getAccountTransactions: (accountId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const queryString = queryParams.toString();
    return api.get(`/banking/accounts/${accountId}/transactions${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getAccountTransactionsPDF: (accountId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    const queryString = queryParams.toString();
    return api.get(`/banking/accounts/${accountId}/transactions/pdf${queryString ? `?${queryString}` : ''}`, { responseType: 'blob' }).then(r => r.data);
  },
  getTransaction: (id) => api.get(`/banking/transactions/${id}`).then(r => r.data),
  createTransaction: (data) => api.post('/banking/transactions', data).then(r => r.data),
  createTransfer: (data) => api.post('/banking/transactions/transfer', data).then(r => r.data),
  updateTransaction: (id, data) => api.put(`/banking/transactions/${id}`, data).then(r => r.data),
  deleteTransaction: (id) => api.delete(`/banking/transactions/${id}`).then(r => r.data),
  voidTransaction: (id, reason) => api.post(`/banking/transactions/${id}/void`, { reason }).then(r => r.data),
};

/**
 * Reconciliation API (Phase 1).
 * Covers column-mapping presets and the CSV statement-import pipeline:
 * parse (light-weight decode + suggest), preview (DB-aware duplicate
 * classification) and commit (persist statement_lines).
 */
export const reconciliationAPI = {
  listMappings: (params = {}) => {
    const q = new URLSearchParams();
    if (params.bank_account_id) q.set('bank_account_id', params.bank_account_id);
    const qs = q.toString();
    return api.get(`/reconciliation/mappings${qs ? `?${qs}` : ''}`).then(r => r.data.mappings);
  },
  getMapping: (id) => api.get(`/reconciliation/mappings/${id}`).then(r => r.data.mapping),
  createMapping: (data) => api.post('/reconciliation/mappings', data).then(r => r.data.mapping),
  updateMapping: (id, data) => api.put(`/reconciliation/mappings/${id}`, data).then(r => r.data.mapping),
  deleteMapping: (id) => api.delete(`/reconciliation/mappings/${id}`).then(r => r.data),

  /**
   * Step 1 of the wizard: upload and decode the file, detect encoding and
   * delimiter, and suggest a column mapping. No DB writes.
   * @param {File} file
   * @param {object} opts - encoding/delimiter/header_row_index overrides
   */
  parseFile: (file, opts = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (opts.encoding) fd.append('encoding', opts.encoding);
    if (opts.delimiter) fd.append('delimiter', opts.delimiter);
    if (opts.header_row_index != null) fd.append('header_row_index', String(opts.header_row_index));
    return api.post('/reconciliation/imports/parse', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  /**
   * Step 3 of the wizard: full preview with duplicate classification.
   * Expects bank_account_id + column_map (and optionally mapping_id,
   * date_format, sign_convention, etc).
   */
  previewImport: (file, body) => {
    const fd = new FormData();
    fd.append('file', file);
    for (const [k, v] of Object.entries(body || {})) {
      if (v == null) continue;
      fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
    }
    return api.post('/reconciliation/imports/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  /** Step 4 of the wizard: commit the parsed file. */
  commitImport: (file, body) => {
    const fd = new FormData();
    fd.append('file', file);
    for (const [k, v] of Object.entries(body || {})) {
      if (v == null) continue;
      fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
    }
    return api.post('/reconciliation/imports/commit', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data);
  },

  listImports: (params = {}) => {
    const q = new URLSearchParams();
    if (params.bank_account_id) q.set('bank_account_id', params.bank_account_id);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return api.get(`/reconciliation/imports${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getImport: (id) => api.get(`/reconciliation/imports/${id}`).then(r => r.data),
  rollbackImport: (id) => api.post(`/reconciliation/imports/${id}/rollback`).then(r => r.data),

  /**
   * Statement line queue. Filters: bank_account_id (required), status,
   * start_date, end_date, amount_min, amount_max, search, import_id,
   * limit, offset.
   */
  listStatementLines: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/reconciliation/statement-lines${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getStatementLine: (id) =>
    api.get(`/reconciliation/statement-lines/${id}`).then(r => r.data),
  updateStatementLineStatus: (id, body) =>
    api.patch(`/reconciliation/statement-lines/${id}/status`, body).then(r => r.data),

  /** Matches (draft -> reconciled -> voided). */
  listMatches: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/reconciliation/matches${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getMatch: (id) => api.get(`/reconciliation/matches/${id}`).then(r => r.data),
  createMatch: (data) => api.post('/reconciliation/matches', data).then(r => r.data),
  updateMatch: (id, data) =>
    api.put(`/reconciliation/matches/${id}`, data).then(r => r.data),
  reconcileMatch: (id) =>
    api.post(`/reconciliation/matches/${id}/reconcile`).then(r => r.data),
  unreconcileMatch: (id, reason) =>
    api.post(`/reconciliation/matches/${id}/unreconcile`, { reason }).then(r => r.data),
  voidMatch: (id, reason) =>
    api.post(`/reconciliation/matches/${id}/void`, { reason }).then(r => r.data),

  /** Allocation fine-grained edits on a draft match. */
  listAllocations: (matchId) =>
    api.get(`/reconciliation/allocations?match_id=${encodeURIComponent(matchId)}`).then(r => r.data),
  createAllocation: (data) =>
    api.post('/reconciliation/allocations', data).then(r => r.data),
  updateAllocation: (id, data) =>
    api.put(`/reconciliation/allocations/${id}`, data).then(r => r.data),
  deleteAllocation: (id) =>
    api.delete(`/reconciliation/allocations/${id}`).then(r => r.data),

  /**
   * Suggestion engine (Phase 2). Returns ranked match suggestions for a
   * single statement line without mutating any state.
   */
  suggest: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/reconciliation/suggest${qs ? `?${qs}` : ''}`).then(r => r.data);
  },

  /**
   * Internal transfers (Phase 2). A transfer links a source statement line
   * on one reconcilable account with a counterpart line on another
   * (bank -> bank / card / loan / drawings).
   */
  listTransfers: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/reconciliation/transfers${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getTransfer: (id) =>
    api.get(`/reconciliation/transfers/${id}`).then(r => r.data),

  /**
   * Find candidate counterpart lines for a transfer. Pass only
   * source_statement_line_id for a cross-account sweep, or include
   * target_bank_account_id to narrow to one account.
   */
  findTransferCounterparts: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return api
      .get(`/reconciliation/transfers/counterparts?${q.toString()}`)
      .then(r => r.data);
  },

  /**
   * Create a transfer. Either supply counterpart_statement_line_id for a
   * matched transfer, or target_bank_account_id for a pending-other-side
   * transfer that will be paired when the counterpart is imported.
   */
  createTransfer: (data) =>
    api.post('/reconciliation/transfers', data).then(r => r.data),

  completeTransfer: (id, data) =>
    api.post(`/reconciliation/transfers/${id}/complete`, data).then(r => r.data),

  voidTransfer: (id, reason) =>
    api.post(`/reconciliation/transfers/${id}/void`, { reason }).then(r => r.data),

  /**
   * Scan pending transfers and auto-complete any with an unambiguous
   * counterpart in the ±7-day window. Call after an import commit.
   */
  autoPairTransfers: (data = {}) =>
    api.post('/reconciliation/transfers/auto-pair', data).then(r => r.data),

  /**
   * Hub dashboard stats (Phase 2). Returns one row per reconcilable account
   * with statement balance / system balance / difference, status counts,
   * uncleared amount, and latest-import metadata in a single roundtrip.
   */
  getAccountStats: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/reconciliation/account-stats${qs ? `?${qs}` : ''}`).then(r => r.data);
  },

  /**
   * Reconciliation status report (plan §11). Per-account counts + amounts
   * for imported / matched / reconciled / unmatched / ignored over a date
   * range, with drill-through back into the workbench.
   */
  getStatusReport: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api.get(`/reconciliation/status-report${qs ? `?${qs}` : ''}`).then(r => r.data);
  },

  /**
   * Loans (Phase 3). A loan_account bank account has an optional
   * loan_account_details row holding lender / principal / rate / term, plus
   * a pre-computed loan_schedule_lines amortisation schedule. These
   * endpoints manage that metadata and feed the loan-repayment modal.
   */
  getLoanAccount: (bankAccountId) =>
    api.get(`/reconciliation/loans/${bankAccountId}`).then(r => r.data),
  upsertLoanDetails: (bankAccountId, data) =>
    api.put(`/reconciliation/loans/${bankAccountId}`, data).then(r => r.data),
  reamortiseLoan: (bankAccountId, data = {}) =>
    api.post(`/reconciliation/loans/${bankAccountId}/reamortise`, data).then(r => r.data),
  previewLoanSchedule: (bankAccountId, data) =>
    api.post(`/reconciliation/loans/${bankAccountId}/preview-schedule`, data).then(r => r.data),
  listLoanSchedule: (bankAccountId, params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api
      .get(`/reconciliation/loans/${bankAccountId}/schedule${qs ? `?${qs}` : ''}`)
      .then(r => r.data);
  },
  getNextLoanLines: (bankAccountId, limit = 3) =>
    api
      .get(`/reconciliation/loans/${bankAccountId}/next-lines?limit=${limit}`)
      .then(r => r.data),
  createLoanRepayment: (data) =>
    api.post('/reconciliation/loans/repayment', data).then(r => r.data),
  createLoanDrawdown: (data) =>
    api.post('/reconciliation/loans/drawdown', data).then(r => r.data),

  /**
   * Credit-card settlements (Phase 3). A settlement is a TWO-statement-line
   * event linking a bank-side outflow with a card-side payment-received,
   * modelled as one credit_card_settlements row + two paired
   * reconciliation_matches (match_type='credit_card_settlement'). Plan §6,
   * §9 anti-double-count rule.
   */
  listCreditCardSettlements: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api
      .get(`/reconciliation/credit-card-settlements${qs ? `?${qs}` : ''}`)
      .then(r => r.data);
  },
  getCreditCardSettlement: (id) =>
    api.get(`/reconciliation/credit-card-settlements/${id}`).then(r => r.data),
  /**
   * Find candidate counterpart lines for a credit-card settlement source
   * line. Restricted to the OPPOSITE side from the source: bank-side source
   * yields card-side candidates and vice versa.
   */
  findCreditCardSettlementCounterparts: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return api
      .get(`/reconciliation/credit-card-settlements/counterparts?${q.toString()}`)
      .then(r => r.data);
  },
  /**
   * Create a settlement. Either supply counterpart_statement_line_id for a
   * matched settlement, or target_bank_account_id for a pending settlement
   * (status pending_card_side / pending_bank_side depending on which side
   * is missing).
   */
  createCreditCardSettlement: (data) =>
    api.post('/reconciliation/credit-card-settlements', data).then(r => r.data),
  completeCreditCardSettlement: (id, data) =>
    api
      .post(`/reconciliation/credit-card-settlements/${id}/complete`, data)
      .then(r => r.data),
  voidCreditCardSettlement: (id, reason) =>
    api
      .post(`/reconciliation/credit-card-settlements/${id}/void`, { reason })
      .then(r => r.data),
  /**
   * Scan pending settlements and auto-complete any with an unambiguous
   * counterpart in the ±7-day window. Call after an import commit.
   */
  autoPairCreditCardSettlements: (data = {}) =>
    api
      .post('/reconciliation/credit-card-settlements/auto-pair', data)
      .then(r => r.data),

  /**
   * Proprietor drawings (Phase 3). A drawings event is one of four
   * flavours keyed by `drawing_type` and always lands on an
   * equity_drawings-class bank_account. Plan §6 Proprietor drawings,
   * §9 "Drawings never hit expense nominals".
   *
   *   - transfer_to_drawings         business bank -> drawings (transfer)
   *   - personal_spend_from_business business bank outflow spent personally
   *   - business_spend_from_personal proprietor paid a business cost
   *   - reimbursement_to_proprietor  business pays proprietor back
   *
   * The backend dispatches on drawing_type; callers supply the relevant
   * ids (source_statement_line_id / counterpart_statement_line_id /
   * linked_purchase_id / clear_log_ids etc.) per flavour.
   */
  createProprietorDrawing: (data) =>
    api.post('/reconciliation/drawings', data).then(r => r.data),
  listProprietorDrawings: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api
      .get(`/reconciliation/drawings${qs ? `?${qs}` : ''}`)
      .then(r => r.data);
  },
  getProprietorDrawing: (id) =>
    api.get(`/reconciliation/drawings/${id}`).then(r => r.data),
  /**
   * Unreimbursed business_spend_from_personal log rows on a drawings
   * account. Powers the reimbursement modal's "select entries to clear"
   * picker.
   */
  listUnreimbursedDrawings: (drawingsAccountId) =>
    api
      .get(`/reconciliation/drawings/unreimbursed?drawings_account_id=${drawingsAccountId}`)
      .then(r => r.data),

  /**
   * Reconciliation rules (Phase 3). Tenant-defined first-match-wins
   * rules with conditions JSONB (description_contains, amount ranges,
   * counterparty, date bounds) and one of four action types:
   * auto_categorise, auto_match_supplier, auto_match_transfer,
   * mark_ignored. Plan §5, §7, §12.
   */
  listRules: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    return api
      .get(`/reconciliation/rules${qs ? `?${qs}` : ''}`)
      .then(r => r.data.rules);
  },
  getRule: (id) =>
    api.get(`/reconciliation/rules/${id}`).then(r => r.data.rule),
  createRule: (data) =>
    api.post('/reconciliation/rules', data).then(r => r.data.rule),
  updateRule: (id, data) =>
    api.put(`/reconciliation/rules/${id}`, data).then(r => r.data.rule),
  deleteRule: (id) =>
    api.delete(`/reconciliation/rules/${id}`).then(r => r.data),
  /**
   * Dry-run a single rule against a statement line. Returns whether the
   * rule's scope + conditions match; no side effects.
   */
  testRule: (id, statementLineId) =>
    api
      .post(`/reconciliation/rules/${id}/test`, { statement_line_id: statementLineId })
      .then(r => r.data),
  /**
   * Evaluate ALL active rules against a line (first-match-wins preview).
   */
  evaluateRules: (statementLineId) =>
    api
      .post('/reconciliation/rules/evaluate', { statement_line_id: statementLineId })
      .then(r => r.data),
  /**
   * Find the highest-priority matching rule for a statement line and
   * apply it. Returns { applied, rule?, outcome? }.
   */
  applyRules: (statementLineId) =>
    api
      .post('/reconciliation/rules/apply', { statement_line_id: statementLineId })
      .then(r => r.data),
  /**
   * Sweep all eligible unmatched/suggested lines on an account and
   * apply the first matching rule to each. Intended for post-import
   * rule runs.
   */
  applyRulesBulk: (data) =>
    api.post('/reconciliation/rules/apply-bulk', data).then(r => r.data),

  /**
   * Month-end close sessions (Phase 3 — see plan §11).
   *
   * A reconciliation session represents a frozen period of bank
   * activity: once finalised the window is read-only until someone
   * explicitly reopens it.
   */
  listSessions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.bank_account_id) q.append('bank_account_id', params.bank_account_id);
    if (params.status) q.append('status', params.status);
    if (params.period_start_from) q.append('period_start_from', params.period_start_from);
    if (params.period_start_to) q.append('period_start_to', params.period_start_to);
    const qs = q.toString();
    return api
      .get(`/reconciliation/sessions${qs ? `?${qs}` : ''}`)
      .then(r => r.data);
  },
  getSession: (id) =>
    api.get(`/reconciliation/sessions/${id}`).then(r => r.data),
  createSession: (data) =>
    api.post('/reconciliation/sessions', data).then(r => r.data),
  updateSession: (id, data) =>
    api.put(`/reconciliation/sessions/${id}`, data).then(r => r.data),
  /**
   * Finalise (lock) a session. After finalisation the covered bank
   * lines / matches cannot be reconciled, unreconciled or voided
   * until reopenSession is called.
   */
  finaliseSession: (id, data = {}) =>
    api.post(`/reconciliation/sessions/${id}/finalise`, data).then(r => r.data),
  reopenSession: (id, data = {}) =>
    api.post(`/reconciliation/sessions/${id}/reopen`, data).then(r => r.data),

  /**
   * Opening-balance mismatch wizard.
   *
   * The GET endpoint returns whether the system-derived opening
   * balance matches the bank statement on the first imported day; the
   * POST endpoint applies the recommended adjusting entry.
   */
  getOpeningBalanceCheck: (bankAccountId) =>
    api
      .get(`/reconciliation/accounts/${bankAccountId}/opening-balance-check`)
      .then(r => r.data),
  fixOpeningBalance: (bankAccountId, data) =>
    api
      .post(`/reconciliation/accounts/${bankAccountId}/opening-balance-fix`, data)
      .then(r => r.data),

  /**
   * Running-balance drift detector — surfaces any dates on which the
   * system ledger disagrees with the imported statement's running
   * balance by more than the configured threshold.
   */
  getRunningBalanceDrift: (bankAccountId, params = {}) => {
    const q = new URLSearchParams();
    if (params.threshold !== undefined) q.append('threshold', params.threshold);
    if (params.start_date) q.append('start_date', params.start_date);
    if (params.end_date) q.append('end_date', params.end_date);
    const qs = q.toString();
    return api
      .get(
        `/reconciliation/accounts/${bankAccountId}/running-balance-drift${qs ? `?${qs}` : ''}`
      )
      .then(r => r.data);
  },

  /**
   * Aggregated liabilities panel — powers the dashboard widget that
   * shows outstanding credit card, loan and drawings balances.
   */
  getLiabilities: () => api.get('/reconciliation/liabilities').then(r => r.data),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats').then(r => r.data),
  getActivity: (limit = 20) => api.get(`/dashboard/activity?limit=${limit}`).then(r => r.data),
};

export const reportsAPI = {
  getProfitAndLoss: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    // Phase 3 toggle: when `reconciled_only=true` the cash-basis
    // section counts only payments matched to imported bank lines.
    if (params.reconciled_only) queryParams.append('reconciled_only', 'true');
    const qs = queryParams.toString();
    return api.get(`/reports/pl${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getBalanceSheet: (as_of) => {
    const qs = as_of ? `?as_of=${encodeURIComponent(as_of)}` : '';
    return api.get(`/reports/balance-sheet${qs}`).then(r => r.data);
  },
  getVATSummary: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const qs = queryParams.toString();
    return api.get(`/reports/vat${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  // VAT Returns
  getVATReturns: () => api.get('/reports/vat-returns').then(r => r.data.returns || r.data),
  createVATReturn: (data) => api.post('/reports/vat-returns', data).then(r => r.data),
  // Trial Balance
  getTrialBalance: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const qs = queryParams.toString();
    return api.get(`/reports/trial-balance${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  // Segmented P&L
  getSegmentedPL: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.period) queryParams.append('period', params.period);
    const qs = queryParams.toString();
    return api.get(`/reports/pl-segmented${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  // Client Monthly Balance
  getClientMonthlyBalance: (year) => {
    const qs = year ? `?year=${year}` : '';
    return api.get(`/reports/client-monthly-balance${qs}`).then(r => r.data);
  },
  // Supplier Monthly Balance
  getSupplierMonthlyBalance: (year) => {
    const qs = year ? `?year=${year}` : '';
    return api.get(`/reports/supplier-monthly-balance${qs}`).then(r => r.data);
  },
  // Debtor/Creditor
  getDebtorCreditor: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.as_of) queryParams.append('as_of', params.as_of);
    if (params.type) queryParams.append('type', params.type);
    const qs = queryParams.toString();
    return api.get(`/reports/debtor-creditor${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  // Client Ageing
  getClientAgeing: (as_of) => {
    const qs = as_of ? `?as_of=${as_of}` : '';
    return api.get(`/reports/client-ageing${qs}`).then(r => r.data);
  },
  // Supplier Ageing
  getSupplierAgeing: (as_of) => {
    const qs = as_of ? `?as_of=${as_of}` : '';
    return api.get(`/reports/supplier-ageing${qs}`).then(r => r.data);
  },
  // Cash-Based P&L
  getCashBasedPL: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    const qs = queryParams.toString();
    return api.get(`/reports/pl-cash-basis${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
};

export const certificatesAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    if (params.clientId) queryParams.append('client_id', params.clientId);
    if (params.isArchived !== undefined) queryParams.append('is_archived', params.isArchived);
    const queryString = queryParams.toString();
    return api.get(`/certificates${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/certificates/${id}`).then(r => r.data),
  create: (data) => api.post('/certificates', data).then(r => r.data),
  update: (id, data) => api.put(`/certificates/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/certificates/${id}`).then(r => r.data),
  archive: (id, isArchived) => api.patch(`/certificates/${id}/archive`, { isArchived }).then(r => r.data),
  getPDF: (id) => api.get(`/certificates/${id}/pdf`, { responseType: 'blob' }).then(r => r.data),
  generatePdfLink: (id) => api.post(`/certificates/${id}/generate-pdf-link`).then(r => r.data),
};

export const ramsAPI = {
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    if (params.link_entity_type) queryParams.append('link_entity_type', params.link_entity_type);
    if (params.link_entity_id) queryParams.append('link_entity_id', params.link_entity_id);
    const queryString = queryParams.toString();
    return api.get(`/rams${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getOne: (id) => api.get(`/rams/${id}`).then(r => r.data),
  create: (data) => api.post('/rams', data).then(r => r.data),
  update: (id, data) => api.put(`/rams/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/rams/${id}`).then(r => r.data),
  clone: (id) => api.post(`/rams/${id}/clone`).then(r => r.data),
  newVersion: (id) => api.post(`/rams/${id}/new-version`).then(r => r.data),
  updateStatus: (id, status) => api.patch(`/rams/${id}/status`, { status }).then(r => r.data),
  getPDF: (id) => api.get(`/rams/${id}/pdf`, { responseType: 'blob' }).then(r => r.data),
  getTemplates: () => api.get('/rams/templates').then(r => r.data),
  getTemplate: (templateId) => api.get(`/rams/templates/${templateId}`).then(r => r.data),
  createTemplate: (data) => api.post('/rams/templates', data).then(r => r.data),
  createFromTemplate: (templateId, data = {}) =>
    api.post(`/rams/templates/${templateId}/rams`, data).then(r => r.data),
};

export const ramsAI = {
  buildHazards: (payload) => api.post('/rams/ai/build-hazards', payload).then(r => r.data),
  buildMethodSteps: (payload) => api.post('/rams/ai/build-method-steps', payload).then(r => r.data),
  improveControls: (payload) => api.post('/rams/ai/improve-controls', payload).then(r => r.data),
  qualityCheck: (payload) => api.post('/rams/ai/quality-check', payload).then(r => r.data),
};

export const tenantUsersAPI = {
  list: (tenantId) => {
    const params = tenantId ? { tenantId } : undefined;
    return api.get('/tenant-users', { params }).then(r => r.data);
  },
  create: (data) => api.post('/tenant-users', data).then(r => r.data),
  update: (id, data) => api.put(`/tenant-users/${id}`, data).then(r => r.data),
  remove: (id, tenantId) => {
    const params = tenantId ? { tenantId } : undefined;
    return api.delete(`/tenant-users/${id}`, { params }).then(r => r.data);
  },
};

export const testEquipmentAPI = {
  getByUser: (userId, tenantId) => {
    const params = tenantId ? { tenantId } : undefined;
    return api.get(`/test-equipment/user/${userId}`, { params }).then(r => r.data);
  },
  create: (data) => api.post('/test-equipment', data).then(r => r.data),
  update: (id, data) => api.put(`/test-equipment/${id}`, data).then(r => r.data),
  delete: (id, tenantId) => {
    const params = tenantId ? { tenantId } : undefined;
    return api.delete(`/test-equipment/${id}`, { params }).then(r => r.data);
  },
};

export const vehiclesAPI = {
  getAll: (params) => api.get('/vehicles', { params }).then(r => r.data),
  getOne: (id) => api.get(`/vehicles/${id}`).then(r => r.data),
  create: (data) => api.post('/vehicles', data).then(r => r.data),
  update: (id, data) => api.put(`/vehicles/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/vehicles/${id}`).then(r => r.data),
  getServices: (id) => api.get(`/vehicles/${id}/services`).then(r => r.data),
  addService: (id, data) => api.post(`/vehicles/${id}/services`, data).then(r => r.data),
  updateService: (vehicleId, serviceId, data) => api.put(`/vehicles/${vehicleId}/services/${serviceId}`, data).then(r => r.data),
  deleteService: (vehicleId, serviceId) => api.delete(`/vehicles/${vehicleId}/services/${serviceId}`).then(r => r.data),
  addReminder: (vehicleId, serviceId, data) => api.post(`/vehicles/${vehicleId}/services/${serviceId}/reminders`, data).then(r => r.data),
  dismissReminder: (reminderId) => api.put(`/vehicles/reminders/${reminderId}/dismiss`).then(r => r.data),
  getMileageLogs: (id) => api.get(`/vehicles/${id}/mileage-logs`).then(r => r.data),
  addMileageLog: (id, data) => api.post(`/vehicles/${id}/mileage-logs`, data).then(r => r.data),
};

export const packagesAPI = {
  getAll: () => api.get('/admin/packages').then(r => r.data),
  getOne: (id) => api.get(`/admin/packages/${id}`).then(r => r.data),
  create: (data) => api.post('/admin/packages', data).then(r => r.data),
  update: (id, data) => api.put(`/admin/packages/${id}`, data).then(r => r.data),
  delete: (id) => api.delete(`/admin/packages/${id}`).then(r => r.data),
};

export const publicAPI = {
  getPackages: () => api.get('/public/packages').then(r => r.data),
  getPackage: (id) => api.get(`/public/packages/${id}`).then(r => r.data),
  signup: (data) => api.post('/public/signup', data).then(r => r.data),
};

export const subscriptionsAPI = {
  getCurrent: () => api.get('/subscriptions/current').then(r => r.data),
  /** Tenant admin: Stripe Checkout for platform subscription (body: { billingCycle?: 'monthly' | 'annual' }). */
  createCheckout: (body = {}) => api.post('/subscriptions/checkout', body).then((r) => r.data),
  cancel: () => api.post('/subscriptions/cancel').then(r => r.data),
  reactivate: () => api.post('/subscriptions/reactivate').then(r => r.data),
  updatePaymentMethod: () => api.post('/subscriptions/update-payment-method').then(r => r.data),
};

export const settingsAPI = {
  get: () => api.get('/settings').then(r => r.data),
  update: (data) => api.put('/settings', data).then(r => r.data),
  getPayments: () => api.get('/settings/payments').then(r => r.data),
  updatePayments: (data) => api.put('/settings/payments', data).then(r => r.data),
  getAI: () => api.get('/settings/ai').then(r => r.data),
  updateAI: (data) => api.put('/settings/ai', data).then(r => r.data),
  getAIUsage: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    const qs = queryParams.toString();
    return api.get(`/settings/ai/usage${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getAIPricingStatus: () => api.get('/settings/ai/pricing-status').then(r => r.data),
  getGoogleCalendarIntegration: () => api.get('/settings/integrations/google-calendar').then(r => r.data),
  updateGoogleCalendarIntegration: (data) => api.put('/settings/integrations/google-calendar', data).then(r => r.data),
};

/** Tenant self-service data export (CSV ZIP). Roles: tenant_admin | tenant_user */
export const exportsAPI = {
  getDatasets: () => api.get('/exports/datasets').then((r) => r.data),
  listJobs: () => api.get('/exports').then((r) => r.data),
  getJob: (id) => api.get(`/exports/${id}`).then((r) => r.data),
  createJob: (body) => api.post('/exports', body).then((r) => r.data),
  /** Full axios response for Content-Disposition filename + blob body */
  downloadZip: (id) =>
    api.get(`/exports/${id}/download`, { responseType: 'blob' }).then((r) => r),
};

export const integrationsAPI = {
  getGoogleStatus: () => api.get('/integrations/google/status').then(r => r.data),
  getGoogleAuthUrl: () => api.get('/integrations/google/auth-url').then(r => r.data),
  disconnectGoogle: () => api.post('/integrations/google/disconnect').then(r => r.data),
};

export const adminAPI = {
  getGlobalSettings: () => api.get('/admin/global-settings').then(r => r.data),
  updateGlobalSetting: (key, value) => api.put(`/admin/global-settings/${key}`, { value }).then(r => r.data),
  getMenuOrder: () => api.get('/admin/menu-order').then(r => r.data),
  updateMenuOrder: (order) => api.put('/admin/menu-order', { order }).then(r => r.data),
  getDependencies: () => api.get('/admin/dependencies').then(r => r.data),
  updateDependencies: (payload) => api.post('/admin/dependencies/update', payload).then(r => r.data),
  getFeedbackTickets: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return api.get(`/admin/feedback${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  updateFeedbackTicket: (id, { status, admin_notes }) =>
    api.patch(`/admin/feedback/${id}`, { status, admin_notes }).then(r => r.data),

  // Error groups (error logging / ticket workflow)
  getErrorGroups: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.excludeStatus) queryParams.append('excludeStatus', params.excludeStatus);
    if (params.severity) queryParams.append('severity', params.severity);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.route) queryParams.append('route', params.route);
    if (params.search) queryParams.append('search', params.search);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return api.get(`/admin/error-groups${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getErrorGroup: (id) => api.get(`/admin/error-groups/${id}`).then(r => r.data),
  getErrorGroupEvents: (id, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return api.get(`/admin/error-groups/${id}/events${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getErrorGroupActivity: (id) => api.get(`/admin/error-groups/${id}/activity`).then(r => r.data),
  updateErrorGroup: (id, data) => api.patch(`/admin/error-groups/${id}`, data).then(r => r.data),
  addErrorGroupComment: (id, comment) =>
    api.post(`/admin/error-groups/${id}/activity`, { comment }).then(r => r.data),
  getErrorsNewCount: () => api.get('/admin/errors/new-count').then(r => r.data),
  markErrorsSeen: () => api.post('/admin/errors/mark-seen').then(r => r.data),
};

export const accountingAPI = {
  // Chart of Accounts
  getAccounts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.type) queryParams.append('type', params.type);
    if (params.active_only !== undefined) queryParams.append('active_only', params.active_only);
    const qs = queryParams.toString();
    return api.get(`/accounting/accounts${qs ? `?${qs}` : ''}`).then(r => r.data.accounts);
  },
  getAccount: (id) => api.get(`/accounting/accounts/${id}`).then(r => r.data),
  createAccount: (data) => api.post('/accounting/accounts', data).then(r => r.data),
  updateAccount: (id, data) => api.put(`/accounting/accounts/${id}`, data).then(r => r.data),
  getAccountBalance: (id, asOfDate) => {
    const qs = asOfDate ? `?asOfDate=${encodeURIComponent(asOfDate)}` : '';
    return api.get(`/accounting/accounts/${id}/balance${qs}`).then(r => r.data);
  },
  getAccountEntries: (id, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return api.get(`/accounting/accounts/${id}/entries${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  
  // Journal Entries
  getJournals: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return api.get(`/accounting/journals${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  getJournal: (id) => api.get(`/accounting/journals/${id}`).then(r => r.data),
  createJournal: (data) => api.post('/accounting/journals', data).then(r => r.data),
  reverseJournal: (id, reason) => api.post(`/accounting/journals/${id}/reverse`, { reason }).then(r => r.data),
  
  // Account Mappings
  getMappings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.mappingType) queryParams.append('mappingType', params.mappingType);
    const qs = queryParams.toString();
    return api.get(`/accounting/mappings${qs ? `?${qs}` : ''}`).then(r => r.data.mappings);
  },
  createMapping: (data) => api.post('/accounting/mappings', data).then(r => r.data),
  updateMapping: (id, data) => api.put(`/accounting/mappings/${id}`, data).then(r => r.data),
  
  // Accounting Entries
  getEntries: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.entryType) queryParams.append('entryType', params.entryType);
    if (params.accountNumber) queryParams.append('accountNumber', params.accountNumber);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    const qs = queryParams.toString();
    return api.get(`/accounting/entries${qs ? `?${qs}` : ''}`).then(r => r.data);
  },
  
  // Seed Chart of Accounts
  seedChartOfAccounts: () => api.post('/accounting/seed-coa').then(r => r.data),
};

export const toolsAPI = {
  getWagoPriceList: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.search) queryParams.append('search', params.search);
    if (params.article_no) queryParams.append('article_no', params.article_no);
    if (params.description) queryParams.append('description', params.description);
    if (params.category) queryParams.append('category', params.category);
    const queryString = queryParams.toString();
    return api.get(`/tools/wago-price-list${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
};

export const plannerAPI = {
  getItems: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.from) queryParams.append('from', params.from);
    if (params.to) queryParams.append('to', params.to);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    if (params.includeGoogle !== undefined) queryParams.append('includeGoogle', params.includeGoogle);
    const queryString = queryParams.toString();
    return api.get(`/planner/items${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  createItem: (data) => api.post('/planner/items', data).then(r => r.data),
  updateItem: (id, data) => api.put(`/planner/items/${id}`, data).then(r => r.data),
  deleteItem: (id, tenantId) =>
    api.delete(`/planner/items/${id}`, { params: tenantId ? { tenantId } : undefined }).then(r => r.data),
  toggleDone: (id, data = {}) => api.post(`/planner/items/${id}/toggle-done`, data).then(r => r.data),
  assignItem: (id, assignedTo, tenantId) =>
    api.post(`/planner/items/${id}/assign`, { assignedTo, ...(tenantId ? { tenantId } : {}) }).then(r => r.data),
  acceptAssignment: (id, tenantId) =>
    api.post(`/planner/items/${id}/accept`, { ...(tenantId ? { tenantId } : {}) }).then(r => r.data),
  rejectAssignment: (id, tenantId) =>
    api.post(`/planner/items/${id}/reject`, { ...(tenantId ? { tenantId } : {}) }).then(r => r.data),
  getAssignedToMe: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    const queryString = queryParams.toString();
    return api.get(`/planner/items/assigned-to-me${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  getAssignedByMe: (tenantId) =>
    api.get('/planner/items/assigned-by-me', { params: tenantId ? { tenantId } : undefined }).then(r => r.data),
  getUpcomingReminders: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.tenantId) queryParams.append('tenantId', params.tenantId);
    const queryString = queryParams.toString();
    return api.get(`/planner/reminders/upcoming${queryString ? `?${queryString}` : ''}`).then(r => r.data);
  },
  dismissReminder: (id, tenantId) =>
    api.post(`/planner/reminders/${id}/dismiss`, { ...(tenantId ? { tenantId } : {}) }).then(r => r.data),
};

/** Tenant knowledge base (authenticated tenants only; not client portal). */
export const knowledgeBaseAPI = {
  listDocuments: (params = {}) => {
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.category) q.set('category', params.category);
    if (params.q) q.set('q', params.q);
    if (params.visibility) q.set('visibility', params.visibility);
    if (params.sort) q.set('sort', params.sort);
    if (params.manufacturer) q.set('manufacturer', params.manufacturer);
    if (params.manufacturer_prefix) q.set('manufacturer_prefix', params.manufacturer_prefix);
    if (params.tags) q.set('tags', params.tags);
    if (params.tag) q.set('tag', params.tag);
    const qs = q.toString();
    return api.get(`/knowledge-base/documents${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  getDocument: (id) => api.get(`/knowledge-base/documents/${id}`).then((r) => r.data),
  listDocumentVersions: (docId) =>
    api.get(`/knowledge-base/documents/${docId}/versions`).then((r) => r.data),
  /** Returns full axios response (blob body + headers). */
  downloadDocument: (id) =>
    api.get(`/knowledge-base/documents/${id}/download`, { responseType: 'blob' }).then((r) => r),
  /** Download a specific historical file version (same auth as document). */
  downloadDocumentVersion: (docId, versionId) =>
    api
      .get(`/knowledge-base/documents/${docId}/versions/${versionId}/download`, {
        responseType: 'blob',
      })
      .then((r) => r),
  uploadDocument: (formData) =>
    api.post('/knowledge-base/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data),
  /** JSON body or FormData (multipart) for metadata + file replace. */
  updateDocument: (id, body) => {
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    return api
      .patch(`/knowledge-base/documents/${id}`, body, {
        ...(isForm ? { headers: { 'Content-Type': 'multipart/form-data' } } : {}),
      })
      .then((r) => r.data);
  },
  deleteDocument: (id, options = {}) => {
    const q = new URLSearchParams();
    if (options.permanent) q.set('permanent', 'true');
    const qs = q.toString();
    return api.delete(`/knowledge-base/documents/${id}${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  manufacturerSuggestions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    const qs = q.toString();
    return api.get(`/knowledge-base/manufacturers/suggestions${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
  tagSuggestions: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.limit != null) q.set('limit', String(params.limit));
    const qs = q.toString();
    return api.get(`/knowledge-base/tags${qs ? `?${qs}` : ''}`).then((r) => r.data);
  },
};

export default api;

